/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {Box, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {COUNTRY_TRANSLATIONS} from '../forms/country-list'

/**
 * AddressDisplay is a React functional component that renders a formatted display of an address.
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.address - The address object containing details to be displayed.
 * @param {string} props.address.firstName - The first name of the person associated with the address.
 * @param {string} props.address.lastName - The last name of the person associated with the address.
 * @param {string} props.address.countryCode - The country code of the address, used for localization.
 * @param {string} props.address.address1 - The primary address line.
 * @param {string} [props.address.address2] - The secondary address line, if applicable.
 * @param {string} props.address.city - The city of the address.
 * @param {string} props.address.stateCode - The state code of the address.
 * @param {string} props.address.postalCode - The postal code of the address.
 * @param {string} props.address.phone - The phone number associated with the address.
 *
 * @returns {JSX.Element} A JSX element that displays the formatted address.
 *
 * The component utilizes the `useIntl` hook to format the country name based on the provided country code.
 * It conditionally renders the second address line if it is provided.
 */
const AddressDisplay = ({address, mb = '16', ...props}) => {
    const intl = useIntl()
    const [formattedAddress, setFormattedAddress] = useState(null)

    useEffect(() => {
        // Imports the postal-address library asynchronously
        const loadPostalAddress = async () => {
            const {default: PostalAddress} = await import('i18n-postal-address')
            const addressInstance = new PostalAddress()
            addressInstance
                .setFirstLastName(`${address.firstName} ${address.lastName}`)
                .setAddress1(address.address1)
                .setCountry(intl.formatMessage(COUNTRY_TRANSLATIONS[address.countryCode]))
                .setCity(address.city)
                .setPostalCode(address.postalCode)
                .setFormat({
                    country: address.countryCode,
                    type: 'personal'
                })

            if (address.address2) {
                addressInstance.setAddress2(address.address2)
            }
            if (address.stateCode) {
                addressInstance.setState(address.stateCode)
            }

            setFormattedAddress(addressInstance.toString())
        }
        loadPostalAddress()
    }, [address, intl])

    return (
        <Box {...props} mb={mb}>
            <Text variant={'bodyBase2'}>
                {address.firstName} {address.lastName}
            </Text>
            <Text variant={'bodyBase2'}>
                {formattedAddress
                    ?.toString()
                    ?.split('\n')
                    ?.map((item, key) => {
                        return (
                            <span key={key}>
                                {item}
                                <br />
                            </span>
                        )
                    })}
            </Text>
            <Text variant={'bodyBase2'}>{address.phone}</Text>
        </Box>
    )
}

AddressDisplay.propTypes = {
    address: PropTypes.object,
    mb: PropTypes.string
}

export default AddressDisplay
